import React, {
  useState,
  useEffect,
  useReducer,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";

import { isSameDay, parseISO, format } from "date-fns";
import openSocket from "../../services/socket-io";
import { useHistory } from "react-router-dom";
import clsx from "clsx";

import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  makeStyles,
} from "@material-ui/core";
import {
  AccessTime,
  Block,
  CallEnd,
  Done,
  DoneAll,
  ErrorOutline,
  ExpandMore,
  GetApp,
  PlayCircleFilled,
  PhoneCallback,
  PhoneInTalk,
  PhoneMissed,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Audio from "../Audio";
import { i18n } from "../../translate/i18n";

const VideoOrAudio = ({ url, className }) => {
  const [isAudioOnly, setIsAudioOnly] = useState(false);
  const [metadataLoaded, setMetadataLoaded] = useState(false);

  useEffect(() => {
    if (!url) return undefined;

    const probe = document.createElement("video");
    probe.preload = "metadata";
    probe.src = url;

    const handleLoadedMetadata = () => {
      setIsAudioOnly(!probe.videoWidth && !probe.videoHeight);
      setMetadataLoaded(true);
      probe.removeAttribute("src");
      probe.load();
    };

    const handleError = () => {
      setMetadataLoaded(true);
      probe.removeAttribute("src");
      probe.load();
    };

    probe.addEventListener("loadedmetadata", handleLoadedMetadata);
    probe.addEventListener("error", handleError);

    return () => {
      probe.removeEventListener("loadedmetadata", handleLoadedMetadata);
      probe.removeEventListener("error", handleError);
      probe.removeAttribute("src");
      probe.load();
    };
  }, [url]);

  if (metadataLoaded && isAudioOnly) {
    return <Audio url={url} mimeType="audio/mp4" />;
  }

  return (
    <video
      className={className}
      src={url}
      controls
      style={!metadataLoaded ? { visibility: "hidden" } : undefined}
    />
  );
};

const parseLocationMessage = (message) => {
  if (message?.mediaType !== "location") return null;

  const body = String(message?.body || "").trim();
  const parts = body
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean);

  let image = "";
  let link = "";
  let description = "";

  if (parts.length >= 2) {
    if (parts[0].startsWith("data:image/")) {
      image = parts[0];
      link = parts[1] || "";
      description = parts.slice(2).join(" | ");
    } else if (/^https?:\/\//i.test(parts[0])) {
      link = parts[0];
      description = parts.slice(1).join(" | ");
    } else if (/^https?:\/\//i.test(parts[1])) {
      link = parts[1];
      description = parts.slice(2).join(" | ") || parts[0];
    }
  }

  if (!link) {
    const coordinateMatch = body.match(
      /(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/
    );
    if (coordinateMatch) {
      const [, latitude, longitude] = coordinateMatch;
      link = `https://maps.google.com/maps?q=${latitude}%2C${longitude}&z=17&hl=pt-BR`;
      description = description || `${latitude}, ${longitude}`;
    }
  }

  return {
    image,
    link,
    description: description || body || "Localização recebida",
  };
};

// Texto de vCard vem com escape ("\;", "\,"), e o nome do WhatsApp pode chegar
// com as partes separadas por ";" - o cartao mostrava "Corporat \;Eder". Na
// tela, o separador vira espaco.
const unescapeVcardText = (value = "") =>
  String(value || "")
    .replace(/\\n/gi, " ")
    .replace(/\\([\\;,:])/g, "$1")
    .replace(/;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const parseVcardMessage = (body = "") => {
  const lines = String(body || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  let contact = "";
  const numbers = [];

  lines.forEach((line) => {
    const separatorIndex = line.indexOf(":");
    if (separatorIndex === -1) return;

    const key = line.slice(0, separatorIndex).toUpperCase();
    const value = line.slice(separatorIndex + 1).trim();

    if (!contact && key.startsWith("FN")) {
      contact = unescapeVcardText(value);
    }

    if (key.startsWith("TEL")) {
      const waidMatch = key.match(/WAID=([0-9]+)/i);
      const number = (waidMatch?.[1] || value).replace(/\D/g, "");
      if (number) {
        numbers.push(number);
      }
    }
  });

  if (!contact) {
    const nameLine = lines.find((line) => line.toUpperCase().startsWith("N:"));
    contact = nameLine ? unescapeVcardText(nameLine.slice(2)) : "";
  }

  return {
    contact: contact || "Contato recebido",
    number: numbers[0] || "",
  };
};

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    background:
      theme.palette.type === "dark"
        ? "radial-gradient(circle at top left, rgba(20,35,31,0.96) 0%, rgba(13,26,23,0.94) 32%, rgba(11,23,20,0.98) 100%)"
        : "radial-gradient(circle at top left, rgba(255,255,255,0.92) 0%, rgba(246,248,252,0.9) 32%, rgba(240,244,248,0.94) 100%)",
  },

  messagesList: {
    backgroundImage: `url(${whatsBackground})`,
    backgroundColor: theme.palette.type === "dark" ? "#18221f" : "#f3ede6",
    backgroundBlendMode:
      theme.palette.type === "dark" ? "soft-light" : "multiply",
    backgroundPosition: "center",
    backgroundRepeat: "repeat",
    backgroundSize: "340px auto",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "22px 20px 20px 20px",
    overflowY: "scroll",
    [theme.breakpoints.down("sm")]: {
      paddingBottom: "90px",
    },
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: theme.palette.primary.main,
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 4,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(19,49,43,0.98) 0%, rgba(15,36,31,0.98) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(249,250,251,0.98) 100%)",
    color: theme.palette.text.primary,
    alignSelf: "flex-start",
    borderTopLeftRadius: 6,
    borderTopRightRadius: 18,
    borderBottomLeftRadius: 18,
    borderBottomRightRadius: 18,
    paddingLeft: 7,
    paddingRight: 7,
    paddingTop: 7,
    paddingBottom: 0,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(52, 211, 153, 0.10)"
        : "rgba(148, 163, 184, 0.18)"
    }`,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 10px 24px rgba(2, 6, 23, 0.24)"
        : "0 10px 24px rgba(15, 23, 42, 0.10)",
  },

  quotedContainerLeft: {
    margin: "0 0 6px 0",
    overflow: "hidden",
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#f0f0f0",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },

  quotedMsg: {
    padding: 10,
    minWidth: 0,
    maxWidth: "100%",
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.type === "dark" ? "#34d399" : "#6bcbef",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 4,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    // Fundo claro mantido de proposito: com o gradiente turquesa escuro do
    // redesenho, os checks de entrega ficavam quase invisiveis. Cor de fundo,
    // cor do texto e cor dos icones formam um conjunto - trocar so o fundo
    // deixaria texto branco sobre verde claro.
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(18,140,126,0.98) 0%, rgba(12,112,100,0.98) 100%)"
        : "linear-gradient(180deg, rgba(230,255,220,0.98) 0%, rgba(214,250,199,0.98) 100%)",
    color: theme.palette.type === "dark" ? "#f8fafc" : "#1f2937",
    alignSelf: "flex-end",
    borderTopLeftRadius: 18,
    borderTopRightRadius: 6,
    borderBottomLeftRadius: 18,
    borderBottomRightRadius: 18,
    paddingLeft: 7,
    paddingRight: 7,
    paddingTop: 7,
    paddingBottom: 0,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(74, 222, 128, 0.18)"
        : "rgba(134, 239, 172, 0.45)"
    }`,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 10px 24px rgba(2, 6, 23, 0.24)"
        : "0 10px 24px rgba(34, 197, 94, 0.12)",
  },
  messageInternalComment: {
    background:
      "linear-gradient(180deg, rgba(255,249,196,0.98) 0%, rgba(255,241,165,0.98) 100%)",
    color: "#5f4b00",
    border: "1px solid rgba(217, 170, 30, 0.50)",
    boxShadow: "0 10px 24px rgba(202, 138, 4, 0.14)",
  },
  messageCallBubble: {
    minWidth: "auto",
    background: "transparent",
    border: 0,
    boxShadow: "none",
    padding: 0,
  },
  messageWithReactions: {
    marginBottom: 16,
  },

  quotedContainerRight: {
    margin: "0 0 6px 0",
    overflow: "hidden",
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "#cfe9ba",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },

  quotedMsgRight: {
    padding: 10,
    minWidth: 0,
    maxWidth: "100%",
    height: "auto",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.primary.main,
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: theme.palette.text.secondary,
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: theme.palette.primary.light,
    fontWeight: 700,
    fontSize: "0.78rem",
    letterSpacing: "0.01em",
    padding: "2px 8px 0 8px",
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "5px 8px 4px 8px",
    lineHeight: 1.45,
  },
  textContentItemCall: {
    padding: "5px 8px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    filter: "grayscale(1)",
    textDecoration: "line-through",
    overflowWrap: "break-word",
    padding: "5px 8px 4px 8px",
  },
  editHistoryItem: {
    marginBottom: 6,
    padding: "5px 8px",
    borderRadius: 8,
    color:
      theme.palette.type === "dark"
        ? "rgba(226,232,240,0.55)"
        : "rgba(75,85,99,0.68)",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(148,163,184,0.08)"
        : "rgba(148,163,184,0.13)",
    textDecoration: "line-through",
    fontStyle: "italic",
  },
  editedBadge: {
    display: "inline-flex",
    alignItems: "center",
    marginRight: 2,
    color: theme.palette.text.secondary,
    fontSize: "0.68rem",
    fontWeight: 700,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  },

  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  stickerMedia: {
    objectFit: "contain",
    width: 150,
    height: 150,
    borderRadius: 8,
    backgroundColor: "transparent",
  },

  timestamp: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 3,
    clear: "both",
    fontSize: 11,
    color: theme.palette.type === "dark" ? "rgba(248,250,252,0.75)" : "#64748b",
    fontWeight: 600,
    lineHeight: 1.2,
    marginTop: 2,
    minHeight: 16,
    whiteSpace: "nowrap",
  },
  mediaOnlyTimestamp: {
    display: "block",
    textAlign: "right",
    fontSize: 11,
    color: theme.palette.type === "dark" ? "rgba(248,250,252,0.75)" : "#64748b",
    fontWeight: 600,
    padding: "2px 8px 4px",
    lineHeight: 1.2,
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    minWidth: "120px",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(19,49,43,0.94)"
        : "rgba(226, 240, 255, 0.92)",
    margin: "10px",
    borderRadius: "999px",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(52, 211, 153, 0.12)"
        : "rgba(125, 170, 230, 0.28)"
    }`,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 8px 16px rgba(2, 6, 23, 0.24)"
        : "0 8px 16px rgba(15, 23, 42, 0.08)",
  },

  dailyTimestampText: {
    color: theme.palette.text.secondary,
    padding: "8px 14px",
    alignSelf: "center",
    marginLeft: "0px",
    fontWeight: 700,
    fontSize: "0.78rem",
  },

  ticketNumberBanner: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    background:
      "linear-gradient(180deg, rgba(255,247,204,0.98) 0%, rgba(255,239,169,0.98) 100%)",
    color: "#8a6300",
    margin: "0 0 14px 0",
    borderRadius: "999px",
    border: "1px solid rgba(226, 192, 84, 0.55)",
    boxShadow: "0 10px 22px rgba(202, 138, 4, 0.16)",
  },

  ticketNumberBannerText: {
    padding: "9px 16px",
    fontWeight: 700,
    fontSize: "0.84rem",
    letterSpacing: "0.02em",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    // Azul de "lida" do WhatsApp, fixo de proposito: o primary.light do tema e
    // esverdeado e ficava indistinguivel do duplo-visto cinza de "entregue" -
    // que e justamente a diferenca que o atendente precisa enxergar de relance.
    color: "#34b7f1",
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  ackErrorIcon: {
    color: theme.palette.error.main,
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },

  reactionsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    position: "absolute",
    bottom: -12,
    zIndex: 2,
    pointerEvents: "none",
  },

  reactionsRowLeft: {
    left: -6,
  },

  reactionsRowRight: {
    right: -6,
  },

  reactionChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    minHeight: 22,
    padding: "1px 7px",
    borderRadius: 999,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(15, 23, 42, 0.92)"
        : "rgba(255,255,255,0.96)",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(148, 163, 184, 0.22)"
    }`,
    fontSize: "0.82rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 8px 16px rgba(2, 6, 23, 0.18)"
        : "0 8px 16px rgba(15, 23, 42, 0.06)",
  },
  templateBadge: {
    display: "inline-flex",
    alignItems: "center",
    margin: "2px 8px 4px 8px",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.02em",
    backgroundColor: "rgba(14, 116, 144, 0.12)",
    color: "#0e7490",
    border: "1px solid rgba(14, 116, 144, 0.28)",
  },
  missingMediaBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    margin: "2px 0 4px 0",
    padding: "4px 9px",
    borderRadius: 8,
    fontSize: "0.74rem",
    fontStyle: "italic",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(251, 146, 60, 0.14)"
        : "rgba(251, 146, 60, 0.16)",
    color: theme.palette.type === "dark" ? "#fdba74" : "#b45309",
    border: "1px solid rgba(251, 146, 60, 0.34)",
  },
  callCard: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: 268,
    maxWidth: "calc(100vw - 96px)",
    margin: "2px 0",
    padding: "8px 10px",
    borderRadius: 12,
    backgroundColor: "var(--call-bg)",
    color: "var(--call-accent)",
    border: "1px solid var(--call-border)",
  },
  callIconCircle: {
    flex: "0 0 auto",
    width: 30,
    height: 30,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--call-accent)",
  },
  callContent: {
    minWidth: 0,
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
  },
  callTitle: {
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
    fontSize: "0.88rem",
    fontWeight: 800,
    lineHeight: 1.15,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  callTime: {
    flex: "0 0 auto",
    alignSelf: "flex-end",
    color: theme.palette.type === "dark" ? "rgba(226,232,240,0.72)" : "#64748b",
    fontSize: 11,
    fontWeight: 700,
  },
  callMeta: {
    flex: "0 0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 4,
  },
  callLiveBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    color: "#15803d",
    fontSize: "0.68rem",
    fontWeight: 800,
    lineHeight: 1,
  },
  callLiveDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: "#22c55e",
    boxShadow: "0 0 0 3px rgba(34, 197, 94, 0.18)",
  },
  callDuration: {
    color: theme.palette.type === "dark" ? "rgba(226,232,240,0.78)" : "#64748b",
    fontSize: "0.68rem",
    fontWeight: 800,
    lineHeight: 1,
  },
  callRecordingButton: {
    flex: "0 0 auto",
    width: 28,
    height: 28,
    padding: 0,
    color: "var(--call-accent)",
  },
  callRecordingDialogTitle: {
    paddingBottom: 0,
    "& h2": {
      fontSize: "1rem",
      fontWeight: 800,
    },
  },
  callRecordingPlayer: {
    width: "100%",
    minWidth: 320,
    maxWidth: "100%",
  },
  instagramEmbedWrapper: {
    marginTop: 8,
    width: 320,
    maxWidth: "100%",
    borderRadius: 10,
    overflow: "hidden",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(148, 163, 184, 0.28)"
        : "rgba(148, 163, 184, 0.35)"
    }`,
    backgroundColor: theme.palette.type === "dark" ? "#0f172a" : "#ffffff",
  },
  instagramEmbedViewport: {
    width: "100%",
    height: 620,
    overflow: "hidden",
    backgroundColor: theme.palette.type === "dark" ? "#000000" : "#000000",
  },
  instagramEmbedFrame: {
    width: "100%",
    height: 1080,
    border: 0,
    display: "block",
    marginTop: -72,
    backgroundColor: theme.palette.type === "dark" ? "#0f172a" : "#ffffff",
  },
  instagramEmbedActions: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "6px 8px 8px 8px",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(15, 23, 42, 0.92)"
        : "rgba(248, 250, 252, 0.94)",
  },
  internalCommentBadge: {
    display: "inline-flex",
    alignItems: "center",
    margin: "2px 8px 4px 8px",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.02em",
    backgroundColor: "rgba(180, 120, 0, 0.12)",
    color: "#8a6300",
    border: "1px solid rgba(202, 138, 4, 0.30)",
  },
  transcriptionBox: {
    margin: "8px 4px 6px",
    padding: "10px 12px",
    borderRadius: 8,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(226, 232, 240, 0.28)"
        : "1px solid rgba(15, 23, 42, 0.45)",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(15, 23, 42, 0.36)"
        : "rgba(255, 255, 255, 0.82)",
    color: theme.palette.text.primary,
    fontSize: "0.94rem",
    lineHeight: 1.45,
    whiteSpace: "pre-wrap",
  },
  transcriptionLabel: {
    display: "block",
    marginBottom: 4,
    fontSize: "0.72rem",
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  ticketEventLink: {
    marginLeft: 6,
    cursor: "pointer",
    textDecoration: "underline",
    color: theme.palette.primary.main,
    fontWeight: 800,
  },
  ticketEventRow: {
    alignSelf: "center",
    margin: "8px 0",
    padding: "6px 12px",
    borderRadius: 999,
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.01em",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(51, 65, 85, 0.45)"
        : "rgba(226, 232, 240, 0.9)",
    color: theme.palette.type === "dark" ? "#e2e8f0" : "#334155",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(148, 163, 184, 0.25)"
        : "rgba(148, 163, 184, 0.35)"
    }`,
  },
}));

const formatTicketNumber = (ticketId) =>
  `Ticket #${String(ticketId || "").padStart(6, "0")}`;

const isWabaTemplateBody = (body) =>
  String(body || "")
    .trim()
    .toLowerCase()
    .startsWith("[template:");

// Marcacao do link de continuacao no WebChat, gravada pelo backend como
// "[Webchat Handoff] | direcao=saida|entrada|retorno | ticket=<id>". Vira uma
// pilula com o outro chamado clicavel - os dois chamados da mesma conversa
// ficam ligados nos dois sentidos.
const WEBCHAT_HANDOFF_PREFIX = "[Webchat Handoff]";

const parseWebchatHandoffBody = (body) => {
  const raw = String(body || "").trim();
  if (!raw.startsWith(WEBCHAT_HANDOFF_PREFIX)) return null;

  const fields = {};
  raw
    .slice(WEBCHAT_HANDOFF_PREFIX.length)
    .split("|")
    .forEach((part) => {
      const [key, ...rest] = part.split("=");
      if (key && rest.length) fields[key.trim()] = rest.join("=").trim();
    });

  const ticketId = Number(fields.ticket || 0);
  if (!ticketId) return null;

  const texts = {
    saida: "Atendimento continuou no WebChat",
    entrada: "Continuação do atendimento do WhatsApp",
    retorno: "Cliente voltou a escrever no WhatsApp",
  };

  return {
    ticketId,
    text: texts[fields.direcao] || "Atendimento ligado ao WebChat",
  };
};

const isWabaCallBody = (body) =>
  String(body || "")
    .trim()
    .toLowerCase()
    .startsWith("[ligacao waba]");

const parseWabaCallBody = (body) => {
  const raw = String(body || "").trim();
  if (!isWabaCallBody(raw)) {
    return null;
  }

  const parts = raw
    .split("|")
    .map((item) => String(item || "").trim())
    .filter(Boolean);

  const dictionary = {};
  parts.forEach((part) => {
    const separatorIndex = part.indexOf("=");
    if (separatorIndex <= 0) return;
    const key = part.slice(0, separatorIndex).trim().toLowerCase();
    const value = part.slice(separatorIndex + 1).trim();
    if (key) {
      dictionary[key] = value;
    }
  });

  const statusRaw = String(dictionary.status || "").toUpperCase();
  const directionRaw = String(
    dictionary.direcao || dictionary.direction || ""
  ).toUpperCase();
  const typeRaw = String(
    dictionary.tipo || dictionary.type || ""
  ).toUpperCase();
  const callId = String(dictionary.call_id || dictionary.callid || "").trim();

  const statusMap = {
    RINGING: "Chamando",
    INITIATED: "Iniciada",
    ACCEPTED: "Atendida",
    CONNECT: "Conectada",
    CONNECTED: "Conectada",
    ANSWERED: "Atendida",
    MISSED: "Não atendida",
    COMPLETED: "Finalizada",
    FAILED: "Falhou",
    BUSY: "Ocupado",
    REJECTED: "Recusada",
    CANCELED: "Cancelada",
  };

  const directionMap = {
    USER_INITIATED: "Recebida",
    BUSINESS_INITIATED: "Originada pela empresa",
    INBOUND: "Recebida",
    OUTBOUND: "Originada",
  };

  const typeMap = {
    VOICE: "Voz",
    VIDEO: "Video",
  };

  const statusPt = statusMap[statusRaw] || statusRaw || "Atualização";
  const directionPt =
    directionMap[directionRaw] || directionRaw || "Não informado";
  const typePt = typeMap[typeRaw] || typeRaw || "Voz";

  return {
    callId,
    statusRaw,
    statusPt,
    directionPt,
    typePt,
  };
};

const getWabaCallVisual = (statusRaw) => {
  if (
    ["MISSED", "FAILED", "BUSY", "REJECTED", "CANCELED"].includes(statusRaw)
  ) {
    return {
      title: "Chamada perdida",
      accent: "#dc2626",
      bg: "rgba(220, 38, 38, 0.12)",
      border: "rgba(220, 38, 38, 0.28)",
      Icon: PhoneMissed,
    };
  }

  if (["COMPLETED"].includes(statusRaw)) {
    return {
      title: "Chamada encerrada",
      accent: "#16a34a",
      bg: "rgba(22, 163, 74, 0.12)",
      border: "rgba(22, 163, 74, 0.28)",
      Icon: CallEnd,
    };
  }

  if (["ACCEPTED", "ANSWERED", "CONNECT", "CONNECTED"].includes(statusRaw)) {
    return {
      title: "Chamada atendida",
      accent: "#16a34a",
      bg: "rgba(22, 163, 74, 0.12)",
      border: "rgba(22, 163, 74, 0.28)",
      Icon: PhoneInTalk,
    };
  }

  if (["INITIATED"].includes(statusRaw)) {
    return {
      title: "Chamada iniciada",
      accent: "#ca8a04",
      bg: "rgba(202, 138, 4, 0.12)",
      border: "rgba(202, 138, 4, 0.30)",
      Icon: PhoneCallback,
    };
  }

  return {
    title: "Chamada recebida",
    accent: "#2563eb",
    bg: "rgba(37, 99, 235, 0.12)",
    border: "rgba(37, 99, 235, 0.28)",
    Icon: PhoneCallback,
  };
};

const formatCallDuration = (totalSeconds) => {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds || 0)));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, "0")}min`;
  }

  if (minutes > 0) {
    return `${minutes}min ${String(remainingSeconds).padStart(2, "0")}s`;
  }

  return `${remainingSeconds}s`;
};

const MARKDOWN_URL_REGEX = /\[[^\]]*]\((https?:\/\/[^\s)]+)\)/i;
const PLAIN_URL_REGEX = /(https?:\/\/[^\s]+)/i;
const INSTAGRAM_HOST_REGEX = /(^|\.)instagram\.com$/i;

const trimTrailingUrlPunctuation = (url) =>
  String(url || "").replace(/[),.;]+$/g, "");

const extractFirstUrlFromText = (text) => {
  const value = String(text || "").trim();
  if (!value) return "";

  const markdownMatch = value.match(MARKDOWN_URL_REGEX);
  if (markdownMatch?.[1]) {
    return trimTrailingUrlPunctuation(markdownMatch[1]);
  }

  const plainMatch = value.match(PLAIN_URL_REGEX);
  if (plainMatch?.[1]) {
    return trimTrailingUrlPunctuation(plainMatch[1]);
  }

  return "";
};

const unwrapMetaRedirectUrl = (url, depth = 0) => {
  if (!url || depth > 3) {
    return "";
  }

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    const redirectedUrl =
      parsed.searchParams.get("u") || parsed.searchParams.get("url");

    if (
      (host === "l.instagram.com" ||
        host === "lm.instagram.com" ||
        host.endsWith("fbsbx.com")) &&
      redirectedUrl
    ) {
      return unwrapMetaRedirectUrl(
        decodeURIComponent(redirectedUrl),
        depth + 1
      );
    }

    return parsed.toString();
  } catch (error) {
    return "";
  }
};

const getInstagramEmbedDataFromBody = (body) => {
  const firstUrl = extractFirstUrlFromText(body);
  if (!firstUrl) {
    return null;
  }

  const normalizedUrl = unwrapMetaRedirectUrl(firstUrl);
  if (!normalizedUrl) {
    return null;
  }

  try {
    const parsed = new URL(normalizedUrl);
    if (!INSTAGRAM_HOST_REGEX.test(parsed.hostname)) {
      return null;
    }

    const segments = parsed.pathname
      .split("/")
      .map((segment) => segment.trim())
      .filter(Boolean);
    const markerIndex = segments.findIndex((segment) =>
      ["reel", "reels", "p", "tv"].includes(segment.toLowerCase())
    );

    if (markerIndex < 0 || !segments[markerIndex + 1]) {
      return null;
    }

    const contentType =
      segments[markerIndex].toLowerCase() === "reels"
        ? "reel"
        : segments[markerIndex].toLowerCase();
    const shortcode = segments[markerIndex + 1];

    return {
      sourceUrl: parsed.toString(),
      embedUrl: `https://www.instagram.com/${contentType}/${shortcode}/embed/captioned/`,
    };
  } catch (error) {
    return null;
  }
};

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
      return [...state];
    }

    // Insere na posicao cronologica em vez de empilhar no fim. Mensagem pode
    // chegar ATRASADA pelo socket - grupo depois de reconexao, sincronizacao em
    // lote - com createdAt anterior ao fim da lista, e o push mandava para o
    // rodape uma mensagem de uma hora atras. O carregamento por HTTP ja vem
    // ordenado, entao o defeito so aparecia no tempo real e sumia ao recarregar.
    //
    // A busca e de tras para frente porque o caso comum e a mensagem nova ser a
    // mais recente: ai para na primeira comparacao.
    const nextTimestamp = new Date(newMessage.createdAt).getTime();
    const nextMessages = [...state];
    let insertAt = nextMessages.length;

    if (!Number.isNaN(nextTimestamp)) {
      while (insertAt > 0) {
        const previous = new Date(nextMessages[insertAt - 1]?.createdAt).getTime();
        if (Number.isNaN(previous) || previous <= nextTimestamp) break;
        insertAt -= 1;
      }
    }

    nextMessages.splice(insertAt, 0, newMessage);

    return nextMessages;
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const MessagesList = ({
  ticketId,
  ticket = null,
  isGroup,
  internalTicketId = null,
  canInteract = true,
  createdMessagesBatch = null,
  peek = false,
}) => {
  const classes = useStyles();
  const history = useHistory();

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();
  const messagesListRef = useRef([]);
  const socketConnectedRef = useRef(false);
  // Guarda a carga inicial em andamento. O lease de socket fabrica um evento
  // "connect" toda vez que um componente se registra com o socket já conectado
  // (services/socket-io.js), entao trocar de ticket dispara handleSocketConnect
  // sem que exista reconexao alguma - e ele refazia a mesma busca que o efeito
  // de troca de ticket já estava fazendo, 4ms antes. Medido em produção: a
  // duplicata custava ~5s no endpoint mais caro.
  const initialLoadTicketRef = useRef(null);

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [callRecordingPlayer, setCallRecordingPlayer] = useState({
    open: false,
    url: "",
    title: "Gravação da chamada",
  });
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);

  useEffect(() => {
    messagesListRef.current = messagesList;
  }, [messagesList]);

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  };

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);

    currentTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    const messages = Array.isArray(createdMessagesBatch?.messages)
      ? createdMessagesBatch.messages
      : [];

    const currentTicketMessages = messages.filter(
      (message) =>
        Number(message.ticketId || message.ticket?.id) ===
        Number(currentTicketId.current)
    );

    if (currentTicketMessages.length) {
      currentTicketMessages.forEach((message) => {
        dispatch({ type: "ADD_MESSAGE", payload: message });
      });
      scrollToBottom();
    }
  }, [createdMessagesBatch]);

  useEffect(() => {
    setLoading(true);
    initialLoadTicketRef.current = ticketId;
    const fetchMessages = async () => {
      try {
        const { data } = await api.get("/messages/" + ticketId, {
          params: {
            pageNumber,
            ...(internalTicketId ? { internalTicketId } : {}),
            // Sem isto o proprio ato de abrir para espiar consumiria o "nao
            // lida" que a fila precisa enxergar.
            ...(peek ? { peek: 1 } : {}),
          },
        });

        if (currentTicketId.current === ticketId) {
          dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
          setHasMore(data.hasMore);
          setLoading(false);
        }

        if (pageNumber === 1 && data.messages.length > 1) {
          scrollToBottom();
        }
      } catch (err) {
        setLoading(false);
        toastError(err);
      } finally {
        // Libera no finally para que uma falha não deixe a guarda presa e
        // bloqueie a recarga de uma reconexao real mais adiante.
        if (initialLoadTicketRef.current === ticketId) {
          initialLoadTicketRef.current = null;
        }
      }
    };

    fetchMessages();
  }, [pageNumber, ticketId, internalTicketId, peek]);

  const refreshCurrentTicketMessages = useCallback(
    async ({ silent = false, scrollOnChange = false } = {}) => {
      try {
        const { data } = await api.get("/messages/" + currentTicketId.current, {
          params: {
            pageNumber: 1,
            ...(internalTicketId ? { internalTicketId } : {}),
            // A atualizacao periodica tambem precisa do parametro: sem ele o
            // ticket ficaria "nao lido" por um instante e seria marcado logo
            // depois, sozinho.
            ...(peek ? { peek: 1 } : {}),
          },
        });

        const receivedMessages = Array.isArray(data.messages)
          ? data.messages
          : [];
        const currentMessagesById = new Map(
          messagesListRef.current.map((message) => [message.id, message])
        );
        const hasChanges = receivedMessages.some((message) => {
          const currentMessage = currentMessagesById.get(message.id);

          return (
            !currentMessage ||
            currentMessage.ack !== message.ack ||
            currentMessage.body !== message.body ||
            currentMessage.mediaUrl !== message.mediaUrl
          );
        });

        if (!hasChanges && silent) {
          setHasMore(data.hasMore);
          return;
        }

        receivedMessages.forEach((message) => {
          const currentMessage = currentMessagesById.get(message.id);
          dispatch({
            type: currentMessage ? "UPDATE_MESSAGE" : "ADD_MESSAGE",
            payload: message,
          });
        });
        setHasMore(data.hasMore);

        if (hasChanges && scrollOnChange) {
          scrollToBottom();
        }
      } catch (err) {
        if (!silent) {
          toastError(err);
        }
      }
    },
    [internalTicketId, peek]
  );

  useEffect(() => {
    const socket = openSocket();
    const joinCurrentChat = () =>
      socket.emit(
        "joinChatBox",
        internalTicketId ? { ticketId, internalTicketId } : ticketId
      );
    const belongsToCurrentTicket = (data) => {
      const payloadTicketId =
        data?.ticket?.id ||
        data?.message?.ticketId ||
        data?.message?.ticket?.id ||
        null;

      return Number(payloadTicketId) === Number(currentTicketId.current);
    };

    const handleSocketConnect = () => {
      socketConnectedRef.current = true;
      joinCurrentChat();

      // Entrar na sala sempre; recarregar so quando não houver carga inicial
      // em andamento para este ticket. Numa reconexao de verdade a guarda esta
      // nula e a recarga acontece normalmente, que e quando ela importa.
      if (initialLoadTicketRef.current === ticketId) {
        return;
      }

      refreshCurrentTicketMessages({ scrollOnChange: true });
    };
    const handleSocketDisconnect = () => {
      socketConnectedRef.current = false;
    };

    socket.on("connect", handleSocketConnect);
    socket.on("disconnect", handleSocketDisconnect);
    socket.on("connect_error", handleSocketDisconnect);
    joinCurrentChat();

    socket.on("appMessage", (data) => {
      if (!belongsToCurrentTicket(data)) {
        return;
      }

      if (data.action === "create") {
        if (data.message) {
          dispatch({ type: "ADD_MESSAGE", payload: data.message });
          scrollToBottom();
          return;
        }

        refreshCurrentTicketMessages();
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    });

    socket.on("ticket", (data) => {
      if (!belongsToCurrentTicket(data)) {
        return;
      }

      const isMessageUpdate =
        data.action === "update" && data.meta?.source === "message";

      if (!isMessageUpdate) {
        return;
      }

      if (data.message) {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
        return;
      }

      refreshCurrentTicketMessages();
    });

    return () => {
      socketConnectedRef.current = false;
      socket.off("connect", handleSocketConnect);
      socket.off("disconnect", handleSocketDisconnect);
      socket.off("connect_error", handleSocketDisconnect);
      socket.disconnect();
    };
  }, [ticketId, internalTicketId, refreshCurrentTicketMessages]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (
        document.hidden ||
        !currentTicketId.current ||
        socketConnectedRef.current
      ) {
        return;
      }

      refreshCurrentTicketMessages({
        silent: true,
        scrollOnChange: true,
      });
    }, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [refreshCurrentTicketMessages]);

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const checkMessageMedia = (message) => {
    const mediaUrl = String(message.mediaUrl || "");
    const mimetype = String(
      message.mimetype || message.mimeType || ""
    ).toLowerCase();
    const isGifMedia =
      /\.gif(?:$|[?#])/i.test(mediaUrl) || mimetype.includes("gif");
    const hasImageExtension = /\.(jpe?g|png|gif|webp)(?:$|[?#])/i.test(
      mediaUrl
    );
    const hasVideoExtension =
      /\.(mp4|webm|mov|m4v|3gp|mpeg|mpg)(?:$|[?#])/i.test(mediaUrl);
    const isAudioMedia =
      message.mediaType === "audio" ||
      message.mediaType === "ptt" ||
      mimetype.startsWith("audio/");
    const isImageMedia =
      message.mediaType === "image" ||
      mimetype.startsWith("image/") ||
      hasImageExtension;
    const isVideoMedia =
      message.mediaType === "video" ||
      mimetype.startsWith("video/") ||
      hasVideoExtension;

    const locationPayload = parseLocationMessage(message);
    if (locationPayload) {
      return (
        <LocationPreview
          image={locationPayload.image}
          link={locationPayload.link}
          description={locationPayload.description}
        />
      );
    } else if (message.mediaType === "vcard") {
      const vcard = parseVcardMessage(message.body);
      return <VcardPreview contact={vcard.contact} numbers={vcard.number} />;
    } else if (message.mediaType === "sticker") {
    /*else if (message.mediaType === "multi_vcard") {
      console.log("multi_vcard")
      console.log(message)
    	
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map(v => (
              <VcardPreview contact={v.name} numbers={v.number} />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
      return (
        <img
          className={classes.stickerMedia}
          src={message.mediaUrl}
          alt="Sticker"
        />
      );
    } else if (isAudioMedia) {
      return (
        <Audio url={message.mediaUrl} mimeType={mimetype || "audio/ogg"} />
      );
    } else if (isImageMedia) {
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    } else if (isGifMedia) {
      return (
        <img
          className={classes.messageMedia}
          src={message.mediaUrl}
          alt="GIF"
          loading="lazy"
        />
      );
    } else if (isVideoMedia) {
      return (
        <VideoOrAudio className={classes.messageMedia} url={message.mediaUrl} />
      );
    } else {
      return (
        <>
          <div className={classes.downloadMedia}>
            <Button
              startIcon={<GetApp />}
              color="primary"
              variant="outlined"
              target="_blank"
              href={message.mediaUrl}
            >
              {message.body || "Download"}
            </Button>
          </div>
          <Divider />
        </>
      );
    }
  };

  const renderAudioTranscription = (message) => {
    const mediaType = String(message.mediaType || "").toLowerCase();
    const mimetype = String(
      message.mimetype || message.mimeType || ""
    ).toLowerCase();
    const isAudioMedia =
      mediaType === "audio" ||
      mediaType === "ptt" ||
      mimetype.startsWith("audio/");

    if (!isAudioMedia) return null;

    if (message.transcription) {
      return (
        <div className={classes.transcriptionBox}>
          <span className={classes.transcriptionLabel}>Transcricao</span>
          {message.transcription}
        </div>
      );
    }

    if (message.transcriptionStatus === "processing") {
      return (
        <div className={classes.transcriptionBox}>
          <span className={classes.transcriptionLabel}>Transcricao</span>
          Transcrevendo audio...
        </div>
      );
    }

    return null;
  };

  const renderMessageAck = (message) => {
    // -1 = recusada pelo WhatsApp. Antes caia no mesmo relogio do pendente, e o
    // atendente nao tinha como saber que aquilo nunca ia sair. O motivo vem numa
    // nota de sistema no thread, logo abaixo da mensagem.
    if (message.ack === -1) {
      return (
        <Tooltip title="Não entregue: o WhatsApp recusou esta mensagem. Veja o motivo na nota abaixo.">
          <ErrorOutline fontSize="small" className={classes.ackErrorIcon} />
        </Tooltip>
      );
    }
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const shouldRenderMediaOnlyTimestamp = (message) =>
    (Boolean(message.mediaUrl) ||
      ["location", "vcard"].includes(message.mediaType)) &&
    (!message.body || shouldHideTechnicalBody(message)) &&
    !message.quotedMsg &&
    message.mediaType !== "internal_comment" &&
    !isWabaCallBody(message.body);

  const shouldHideTechnicalBody = (message) =>
    ["location", "vcard"].includes(message.mediaType);

  const shouldRenderMessageBody = (message) =>
    Boolean(
      (!shouldHideTechnicalBody(message) && message.body) ||
        message.quotedMsg ||
        message.isDeleted ||
        message.mediaType === "internal_comment" ||
        (!message.mediaUrl && !shouldHideTechnicalBody(message))
    );

  // A media message whose file never got stored renders as an empty bubble with
  // only a timestamp, because `checkMessageMedia` is gated on mediaUrl while
  // `shouldRenderMessageBody` turns true precisely *because* mediaUrl is absent.
  // vcard and location are excluded: they legitimately carry no mediaUrl.
  const isMissingMedia = (message) =>
    ["image", "video", "audio", "ptt", "sticker", "document"].includes(
      String(message.mediaType || "").toLowerCase()
    ) && !message.mediaUrl;

  const renderMissingMedia = (message) => {
    if (!isMissingMedia(message)) return null;

    return (
      <span className={classes.missingMediaBadge}>
        <Block fontSize="inherit" />
        Midia não recebida
      </span>
    );
  };

  const renderEditHistory = (message) => {
    const history = Array.isArray(message.editHistory)
      ? message.editHistory
      : [];
    if (!history.length) return null;

    return history.map((version) => (
      <div
        key={version.id}
        className={classes.editHistoryItem}
        title={version.editedByName || undefined}
      >
        {version.previousBody}
      </div>
    ));
  };

  const renderMediaOnlyTimestamp = (message) => {
    if (!shouldRenderMediaOnlyTimestamp(message)) return null;

    return (
      <span className={classes.mediaOnlyTimestamp}>
        {format(parseISO(message.createdAt), "HH:mm")}
        {message.fromMe && renderMessageAck(message)}
      </span>
    );
  };

  const renderMessageReactions = (message) => {
    if (!Array.isArray(message?.reactions) || !message.reactions.length) {
      return null;
    }

    const groupedReactions = message.reactions.reduce((acc, item) => {
      if (!item?.reaction) {
        return acc;
      }

      acc[item.reaction] = (acc[item.reaction] || 0) + 1;
      return acc;
    }, {});

    const reactionEntries = Object.entries(groupedReactions);

    if (!reactionEntries.length) {
      return null;
    }

    return (
      <div
        className={clsx(classes.reactionsRow, {
          [classes.reactionsRowLeft]: !message.fromMe,
          [classes.reactionsRowRight]: message.fromMe,
        })}
      >
        {reactionEntries.map(([emoji, count]) => (
          <span key={`${message.id}-${emoji}`} className={classes.reactionChip}>
            <span>{emoji}</span>
            {count > 1 ? <span>{count}</span> : null}
          </span>
        ))}
      </div>
    );
  };

  const hasMessageReactions = (message) =>
    Array.isArray(message?.reactions) && message.reactions.length > 0;

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    }
    if (index < messagesList.length - 1) {
      let messageDay = parseISO(messagesList[index].createdAt);
      let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    }
    if (index === messagesList.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div
          className={clsx(classes.quotedMsg, {
            [classes.quotedMsgRight]: message.fromMe,
          })}
        >
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}
          {message.quotedMsg?.body}
        </div>
      </div>
    );
  };

  const renderInstagramSharedPreview = (message) => {
    if (message.mediaUrl) {
      return null;
    }

    const embedData = getInstagramEmbedDataFromBody(message.body);
    if (!embedData) {
      return null;
    }

    return (
      <div className={classes.instagramEmbedWrapper}>
        <div className={classes.instagramEmbedViewport}>
          <iframe
            className={classes.instagramEmbedFrame}
            src={embedData.embedUrl}
            title={`instagram-embed-${message.id}`}
            loading="lazy"
            scrolling="no"
            referrerPolicy="no-referrer-when-downgrade"
            allow="encrypted-media; picture-in-picture; web-share"
          />
        </div>
        <div className={classes.instagramEmbedActions}>
          <Button
            color="primary"
            size="small"
            target="_blank"
            rel="noopener noreferrer"
            href={embedData.sourceUrl}
          >
            Abrir no Instagram
          </Button>
        </div>
      </div>
    );
  };

  const isInstagramSharedEmbedMessage = (message) => {
    if (!message || message.mediaUrl) {
      return false;
    }

    const body = String(message.body || "").trim();
    if (!body) {
      return false;
    }

    if (!getInstagramEmbedDataFromBody(body)) {
      return false;
    }

    const normalizedBody = body.replace(/\s+/g, " ").trim();
    const isMarkdownOnlyUrl = /^\[[^\]]*]\((https?:\/\/[^\s)]+)\)$/i.test(
      normalizedBody
    );
    const isPlainOnlyUrl = /^https?:\/\/\S+$/i.test(normalizedBody);

    return isMarkdownOnlyUrl || isPlainOnlyUrl;
  };

  const renderWabaCallBadge = (message) => {
    const callData = parseWabaCallBody(message.body);
    if (!callData) {
      return <MarkdownWrapper>{message.body}</MarkdownWrapper>;
    }

    const visual = getWabaCallVisual(callData.statusRaw);
    const Icon = visual.Icon;
    const activeStatuses = ["ACCEPTED", "ANSWERED", "CONNECT", "CONNECTED"];
    const terminalStatuses = [
      "COMPLETED",
      "MISSED",
      "FAILED",
      "BUSY",
      "REJECTED",
      "CANCELED",
    ];
    const messageTime = new Date(message.createdAt).getTime();
    const answeredMessage = messagesList
      .filter((otherMessage) => {
        if (!otherMessage || !isWabaCallBody(otherMessage.body)) {
          return false;
        }

        const otherCallData = parseWabaCallBody(otherMessage.body);
        if (!otherCallData || otherCallData.callId !== callData.callId) {
          return false;
        }

        return activeStatuses.includes(otherCallData.statusRaw);
      })
      .sort(
        (firstMessage, secondMessage) =>
          new Date(firstMessage.createdAt).getTime() -
          new Date(secondMessage.createdAt).getTime()
      )[0];
    const callDurationSeconds =
      callData.callId && callData.statusRaw === "COMPLETED" && answeredMessage
        ? Math.max(
            0,
            Math.round(
              (messageTime - new Date(answeredMessage.createdAt).getTime()) /
                1000
            )
          )
        : null;
    const isActiveCall =
      callData.callId &&
      activeStatuses.includes(callData.statusRaw) &&
      !messagesList.some((otherMessage) => {
        if (
          !otherMessage ||
          otherMessage.id === message.id ||
          !isWabaCallBody(otherMessage.body)
        ) {
          return false;
        }

        const otherCallData = parseWabaCallBody(otherMessage.body);
        if (!otherCallData || otherCallData.callId !== callData.callId) {
          return false;
        }

        const otherTime = new Date(otherMessage.createdAt).getTime();
        return (
          terminalStatuses.includes(otherCallData.statusRaw) &&
          otherTime >= messageTime
        );
      });
    const recording = message.callRecording;
    const canPlayRecording =
      recording && recording.id && recording.status === "available";

    const handlePlayRecording = async () => {
      try {
        const { data } = await api.get(
          `/voice/call-recordings/${recording.id}/playback`
        );
        if (data?.url) {
          setCallRecordingPlayer({
            open: true,
            url: data.url,
            title: `${visual.title} - ${format(
              parseISO(message.createdAt),
              "HH:mm"
            )}`,
          });
        }
      } catch (err) {
        toastError(err);
      }
    };

    return (
      <div
        className={classes.callCard}
        style={{
          "--call-accent": visual.accent,
          "--call-bg": visual.bg,
          "--call-border": visual.border,
        }}
      >
        <span className={classes.callIconCircle}>
          <Icon style={{ fontSize: 22 }} />
        </span>
        <span className={classes.callContent}>
          <span className={classes.callTitle}>{visual.title}</span>
        </span>
        <span className={classes.callMeta}>
          {canPlayRecording && (
            <IconButton
              className={classes.callRecordingButton}
              size="small"
              onClick={handlePlayRecording}
              title="Ouvir gravação"
            >
              <PlayCircleFilled style={{ fontSize: 22 }} />
            </IconButton>
          )}
          {isActiveCall && (
            <span className={classes.callLiveBadge}>
              <span className={classes.callLiveDot} />
              Em ligação
            </span>
          )}
          {callDurationSeconds !== null && (
            <span className={classes.callDuration}>
              {formatCallDuration(callDurationSeconds)}
            </span>
          )}
          <span className={classes.callTime}>
            {format(parseISO(message.createdAt), "HH:mm")}
          </span>
        </span>
      </div>
    );
  };

  const getTicketEventBody = (message) => {
    const body = String(message?.body || "");
    const closedByType = String(ticket?.closedByType || "").toLowerCase();
    const isClosedByAi =
      closedByType === "ai" ||
      (closedByType === "" &&
        String(ticket?.handledByMode || "").toLowerCase() === "ai");

    if (
      message?.mediaType === "ticket_event" &&
      isClosedByAi &&
      /^Fechado por:/i.test(body.trim())
    ) {
      return "Fechado por: Inteligencia Artificial";
    }

    return body;
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        const isInternalComment = message.mediaType === "internal_comment";
        const isTicketEvent = message.mediaType === "ticket_event";
        const hasInstagramSharedEmbed = isInstagramSharedEmbedMessage(message);

        if (isTicketEvent) {
          const handoff = parseWebchatHandoffBody(message.body);
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              <span className={classes.ticketEventRow}>
                {handoff ? (
                  <>
                    {handoff.text}
                    <span
                      role="link"
                      tabIndex={0}
                      className={classes.ticketEventLink}
                      onClick={() => history.push(`/tickets/${handoff.ticketId}`)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          history.push(`/tickets/${handoff.ticketId}`);
                        }
                      }}
                    >
                      #{handoff.ticketId}
                    </span>
                  </>
                ) : (
                  getTicketEventBody(message)
                )}
              </span>
            </React.Fragment>
          );
        }

        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div
                className={clsx(classes.messageLeft, {
                  [classes.messageInternalComment]: isInternalComment,
                  [classes.messageWithReactions]: hasMessageReactions(message),
                  [classes.messageCallBubble]:
                    isWabaCallBody(message.body) || hasInstagramSharedEmbed,
                })}
              >
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted || !canInteract}
                  className={classes.messageActionsButton}
                  style={!canInteract ? { display: "none" } : undefined}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}
                {(message.mediaUrl ||
                  message.mediaType === "location" ||
                  message.mediaType === "vcard") &&
                  //|| message.mediaType === "multi_vcard"
                  checkMessageMedia(message)}
                {renderMediaOnlyTimestamp(message)}
                {renderAudioTranscription(message)}
                {shouldRenderMessageBody(message) && (
                  <div
                    className={clsx(classes.textContentItem, {
                      [classes.textContentItemCall]: isWabaCallBody(
                        message.body
                      ),
                    })}
                  >
                    {message.quotedMsg && renderQuotedMessage(message)}
                    {renderEditHistory(message)}
                    {renderMissingMedia(message)}
                    {isInternalComment && (
                      <span className={classes.internalCommentBadge}>
                        Comentario interno
                      </span>
                    )}
                    {isWabaCallBody(message.body) ? (
                      renderWabaCallBadge(message)
                    ) : hasInstagramSharedEmbed ? (
                      renderInstagramSharedPreview(message)
                    ) : (
                      <>
                        {!shouldHideTechnicalBody(message) && (
                          <MarkdownWrapper>{message.body}</MarkdownWrapper>
                        )}
                        {renderInstagramSharedPreview(message)}
                      </>
                    )}
                    {!isWabaCallBody(message.body) && (
                      <span className={classes.timestamp}>
                        {message.isEdited && (
                          <span className={classes.editedBadge}>
                            {i18n.t("messageOptionsMenu.edited")}
                          </span>
                        )}
                        {format(parseISO(message.createdAt), "HH:mm")}
                      </span>
                    )}
                  </div>
                )}
                {renderMessageReactions(message)}
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div
                className={clsx(classes.messageRight, {
                  [classes.messageInternalComment]: isInternalComment,
                  [classes.messageWithReactions]: hasMessageReactions(message),
                  [classes.messageCallBubble]:
                    isWabaCallBody(message.body) || hasInstagramSharedEmbed,
                })}
              >
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted || !canInteract}
                  className={classes.messageActionsButton}
                  style={!canInteract ? { display: "none" } : undefined}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {(message.mediaUrl ||
                  message.mediaType === "location" ||
                  message.mediaType === "vcard") &&
                  //|| message.mediaType === "multi_vcard"
                  checkMessageMedia(message)}
                {renderMediaOnlyTimestamp(message)}
                {renderAudioTranscription(message)}
                {shouldRenderMessageBody(message) && (
                  <div
                    className={clsx(classes.textContentItem, {
                      [classes.textContentItemDeleted]: message.isDeleted,
                      [classes.textContentItemCall]: isWabaCallBody(
                        message.body
                      ),
                    })}
                  >
                    {message.isDeleted && (
                      <Block
                        color="disabled"
                        fontSize="small"
                        className={classes.deletedIcon}
                      />
                    )}
                    {message.quotedMsg && renderQuotedMessage(message)}
                    {renderEditHistory(message)}
                    {renderMissingMedia(message)}
                    {isInternalComment && (
                      <span className={classes.internalCommentBadge}>
                        Comentario interno
                      </span>
                    )}
                    {(message.templateName ||
                      isWabaTemplateBody(message.body)) && (
                      <span className={classes.templateBadge}>
                        {message.templateName
                          ? `Template WABA · ${message.templateName}`
                          : "Template WABA"}
                      </span>
                    )}
                    {isWabaCallBody(message.body) ? (
                      renderWabaCallBadge(message)
                    ) : hasInstagramSharedEmbed ? (
                      renderInstagramSharedPreview(message)
                    ) : (
                      <>
                        {!shouldHideTechnicalBody(message) && (
                          <MarkdownWrapper>{message.body}</MarkdownWrapper>
                        )}
                        {renderInstagramSharedPreview(message)}
                      </>
                    )}
                    {!isWabaCallBody(message.body) && (
                      <span className={classes.timestamp}>
                        {message.isEdited && (
                          <span className={classes.editedBadge}>
                            {i18n.t("messageOptionsMenu.edited")}
                          </span>
                        )}
                        {format(parseISO(message.createdAt), "HH:mm")}
                        {renderMessageAck(message)}
                      </span>
                    )}
                  </div>
                )}
                {renderMessageReactions(message)}
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Say hello to your new contact!</div>;
    }
  };

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
        readOnly={!canInteract}
        connectionType={ticket?.whatsapp?.connectionType}
      />
      <Dialog
        open={callRecordingPlayer.open}
        onClose={() =>
          setCallRecordingPlayer({
            open: false,
            url: "",
            title: "Gravação da chamada",
          })
        }
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className={classes.callRecordingDialogTitle}>
          {callRecordingPlayer.title}
        </DialogTitle>
        <DialogContent>
          {callRecordingPlayer.url ? (
            <audio
              className={classes.callRecordingPlayer}
              src={callRecordingPlayer.url}
              controls
              autoPlay
            />
          ) : null}
        </DialogContent>
      </Dialog>
      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        <span className={classes.ticketNumberBanner}>
          <div className={classes.ticketNumberBannerText}>
            {formatTicketNumber(ticketId)}
          </div>
        </span>
        {messagesList.length > 0 ? renderMessages() : []}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;
